import { JSONContent } from "@markit/common.types";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import CustomImage from "../components/TextFields/RichTextEditorOptions/CustomImage";
import Typography from "@tiptap/extension-typography";
import { CharacterCount, Placeholder } from "@tiptap/extensions";
import { ThemeStyle } from "./useTheme";

type useRichTextEditorProps = {
  body: JSONContent | undefined;
  styles: string;
  fontSize?: number;
  isReadOnly?: boolean;
  addImageExtension?: boolean;
  addCharacterCountExtension?: {
    include: boolean;
    limit: number;
  };
  alternatePlaceholder?: string;
  theme?: ThemeStyle;
};

// Custom hook to handle the rich text editor on drafting and viewing files
export const useRichTextEditor = (props: useRichTextEditorProps) => {
  const {
    body,
    styles,
    addImageExtension,
    addCharacterCountExtension,
    isReadOnly,
    alternatePlaceholder,
    theme,
    fontSize = 14, // Default to 14px if not provided
  } = props;

  const editor = useEditor(
    isReadOnly
      ? {
          extensions: [StarterKit, ...(addImageExtension ? [CustomImage] : [])],
          content: body || "",
          editable: false,
          editorProps: {
            attributes: {
              style: `color: ${theme?.PrimaryText.color}; line-height: 1.5; font-size: ${fontSize}px;`,
            },
          },
        }
      : {
          extensions: [
            StarterKit.configure({
              link: {
                openOnClick: false,
                autolink: false,
              },
            }),
            Typography,
            Placeholder.configure({
              placeholder: () => {
                return alternatePlaceholder || "Start writing...";
              },
            }),
            ...(addImageExtension ? [CustomImage] : []),
            ...(addCharacterCountExtension?.include
              ? [
                  CharacterCount.configure({
                    limit: addCharacterCountExtension.limit,
                  }),
                ]
              : []),
          ],
          editorProps: {
            attributes: {
              class: "tiptap",
              style: `${styles}; font-size: ${fontSize}px;`,
            },
          },
          content: body || "",
        }
  );

  return {
    editor,
  };
};
